import { type ChangeEvent, type ElementRef, type ReactElement, useRef, useState } from "react"

import { useTranslation } from "react-i18next"
import { useInView } from "react-intersection-observer"
import { useInfiniteQuery, useQueries, useQuery } from "react-query"
import { useParams } from "react-router-dom"

import CloseIcon from "@mui/icons-material/Close"
import { Dialog, DialogContent, DialogTitle, IconButton, useMediaQuery, useTheme } from "@mui/material"
import type { InputChangeEvent } from "@synapse-analytics/synapse-ui"
import { Typography } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"
import { isEmpty } from "lodash"

import { getTheme } from "../../hooks/UseTheme"
import { useDebounce } from "../../hooks/useDebounce"
import { KonanAPI } from "../../services/KonanAPI"
import type { DeploymentList } from "../../types/generated/api/DeploymentList"
import type { PaginatedDeploymentListList } from "../../types/generated/api/PaginatedDeploymentListList"
import { extractPageFromBackEndPaginationLink } from "../../utils/genericHelpers"
import { DATA_FETCHING_QUERY_FUNCTIONS_MAPPER, LISTING_QUERY_FUNCTIONS_MAPPER } from "./Constants"
import { constructComponentInitialFormikValues } from "./Helpers"
import type { ComponentTypeExtractor, CopyComponentFlowProps, SelectedComponents, Variant } from "./Interfaces"
import { Audit } from "./Steps/Audit"
import { Selection } from "./Steps/Selection"

import styles from "./ImportDialog.module.scss"

/**
 * The CopyComponentsFlow component is responsible for handling the import flow of components
 * within a dialog. It manages the selection and editing of components from different projects,
 * allowing users to select components from existing projects, modify them, and import them into
 * a new project. It leverages various hooks to manage state and fetch data, including projects and
 * components lists. The component supports pagination and filtering, and provides handlers to
 * manage component selection, import steps, and component removal. The import process is divided
 * into steps using the Selection and Edit components.
 *
 * @template TVariant - A type that extends the Variant interface, used to specify the type of components.
 * @param {Readonly<CopyComponentFlowProps<TVariant>>} props - The props for the CopyComponentsFlow component, including:
 * @param {TVariant} props.componentName - The name of the component variant to be copied.
 * @param {boolean} props.isDialogOpen - A boolean indicating whether the dialog is open.
 * @param {Dispatch<SetStateAction<boolean>>} props.setIsDialogOpen - A function to set the dialog's open state.
 * @returns {ReactElement} - The rendered CopyComponentsFlow component.
 */

export function CopyComponentsFlow<TVariant extends Variant>(
  props: Readonly<CopyComponentFlowProps<TVariant>>,
): ReactElement {
  const { componentName, isDialogOpen, setIsDialogOpen } = props

  //#region  Hooks
  const { id: projectId } = useParams<{ id: string }>()

  const MuiTheme = useTheme()
  const theme = getTheme()

  // Configure intersection observer for infinite scroll functionality
  // Only triggers next page fetch when last item is in view and more data is available
  const { ref: lastProjectRef } = useInView({
    onChange: (inView: boolean) => {
      // Fetch next page only when: element is in view AND has more pages AND not currently fetching
      inView && hasNextPage && !isProjectsFetching && !isFetchingNextPage && fetchNextPage()
    },
  })

  // This ref for accessing the dialog height when the screen hight changes
  const currProjectComponentsListRef = useRef<ElementRef<"div">>(null)

  // This ref to hold the components data when they are fetched and it is updated once any change happens in any component
  const allComponentsDataRef = useRef<{ [compId: string]: ComponentTypeExtractor<TVariant, "formikValues"> }>({})

  // selected project details
  const [selectedProject, setSelectedProject] = useState<{ name: string; id: string }>()
  // current project components page
  const [currentProjCompPage, setCurrentProjCompPage] = useState<number>(1)
  // number of component pages
  const [numberOfPageItems, setNumberOfPageItems] = useState<number>(10)
  const [searchValue, setSearchValue] = useState<string | undefined>(undefined)
  // selected components state
  const [selectedComponents, setSelectedComponents] = useState<SelectedComponents>()
  // current flow step state
  const [currentImportStep, setCurrentImportStep] = useState<number>(1)
  // current selected project state
  const [currentProjectStep, setCurrentProjectStep] = useState<number>(1)

  const { t } = useTranslation("common")
  //#endregion

  //#region Fetch
  const {
    isLoading: isProjectsLoading,
    isFetching: isProjectsFetching,
    data: projects,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery<AxiosResponse<PaginatedDeploymentListList>, AxiosError, DeploymentList>(
    [`projects`, searchValue],
    ({ pageParam = 1 }) =>
      KonanAPI.fetchProjects({
        page: pageParam,
        category: "workflow",
        search: searchValue,
      }),
    {
      select: ({ pageParams, pages }) => ({
        pageParams: pageParams,
        pages: pages
          .reduce<
            DeploymentList[]
          >((accumulator, page) => accumulator.concat(page.data.results as DeploymentList[]), [])
          .filter((project) => project.uuid !== projectId),
      }),
      getNextPageParam: (lastPage) => {
        return lastPage.data?.next ? extractPageFromBackEndPaginationLink(lastPage.data?.next) : false
      },
      enabled: !!isDialogOpen,
      refetchOnMount: false,
    },
  )

  // Fetch components list for selected project
  const { isFetching: isComponentsListLoading, data: componentsRes } = useQuery<
    AxiosResponse<ComponentTypeExtractor<TVariant, "listingQueryFn">>,
    AxiosError
  >(
    ["componentsList", componentName, selectedProject, currentProjCompPage],
    () =>
      LISTING_QUERY_FUNCTIONS_MAPPER[componentName](
        selectedProject?.id as string,
        currentProjCompPage,
        numberOfPageItems,
      ),
    {
      onSuccess: () => {
        setNumberOfPageItems(
          currProjectComponentsListRef.current?.clientHeight
            ? //subtract the pagination element height and divide by item height
              Math.floor((currProjectComponentsListRef.current?.clientHeight - 48) / 38)
            : 10,
        )
      },
      enabled: !!selectedProject?.id && !!isDialogOpen,
    },
  )
  //#endregion

  //#region Handlers
  const searchInputHandler = (e: InputChangeEvent): void => {
    debouncedOnChange(e.target.value as string)
  }

  const debouncedOnChange = useDebounce(setSearchValue, 400)

  // This handler handle selecting and deselecting a component with check box
  const checkboxChangeHandler = (component: { uuid: string }, e: ChangeEvent<HTMLInputElement>): void => {
    setSelectedComponents((prev) => {
      if (e.currentTarget.checked) {
        if (!selectedProject?.id) return { ...prev }

        if (prev?.[selectedProject.id] === undefined) {
          return { ...prev, [selectedProject.id]: { projName: selectedProject.name, components: [component.uuid] } }
        }

        prev[selectedProject.id]?.components.push(component.uuid)

        return { ...prev }
      } else {
        if (!prev || !selectedProject?.id) return

        const componentIndex = prev[selectedProject.id]?.components.indexOf(component.uuid)

        if (typeof componentIndex === "number" && componentIndex > -1) {
          prev[selectedProject.id]?.components.splice(componentIndex, 1)
          if (prev[selectedProject.id]?.components.length === 0) delete prev[selectedProject.id]
        }

        return { ...prev }
      }
    })
  }

  const importBtnClickHandler = (): void => {
    if (currentImportStep < importSteps.length) setCurrentImportStep((prev) => ++prev)
  }

  // Move to the next project
  const incrementProjectStep = (): void => {
    if (currentProjectStep === numOfTotalSelectedProjects) {
      setIsDialogOpen(false)
    } else {
      setCurrentProjectStep((prev) => ++prev)
    }
  }

  // remove the component when it is saved or dismissed
  // and if all components in the current project are removed
  // increment step
  const removeComponent = (id: string): void => {
    setSelectedComponents((prev): SelectedComponents => {
      if (!prev || !currentEditProjectData?.[0]) return
      const currentProjectComponents = prev[currentEditProjectData[0]]?.components
      const componentIndex = currentProjectComponents?.indexOf(id)

      if (componentIndex != undefined && componentIndex > -1) {
        currentProjectComponents?.splice(componentIndex, 1)

        if (currentProjectComponents?.length === 0) {
          incrementProjectStep()
        }
      }

      return { ...prev }
    })
  }
  //#endregion

  //#region variables
  // map the selected project key and value to an array where the project Id is at index 0
  // and project details at index 1 to use these data to access and get the rest of
  // the necessary data
  const currentEditProjectData =
    !!selectedComponents && !isEmpty(selectedComponents)
      ? Object.entries(selectedComponents)[currentProjectStep - 1]
      : undefined

  // Calculate the total number of all the selected components for all projects
  const numOfTotalSelectedComponents =
    selectedComponents && Object.values(selectedComponents).reduce((acc, value) => acc + value.components.length, 0)

  // Calculate the total number of selected projects
  const numOfTotalSelectedProjects = selectedComponents && Object.keys(selectedComponents).length

  // Fetch Current Project Components data and construct the initial formik values object
  const componentsDataResults = useQueries(
    currentEditProjectData
      ? currentEditProjectData[1].components.map((componentId) => ({
          queryKey: ["componentData", componentId],
          queryFn: () => DATA_FETCHING_QUERY_FUNCTIONS_MAPPER[componentName](currentEditProjectData[0], componentId),
          onSuccess: ({ data }: { data: ComponentTypeExtractor<TVariant, "componentData"> }) => {
            const componentData = constructComponentInitialFormikValues(data)

            if (!componentData) return

            allComponentsDataRef.current[data.uuid] = componentData
          },
          enabled: currentImportStep === 2,
        }))
      : [],
  )
  //#endregion

  //#region Steps
  const importSteps = [
    <Selection<TVariant>
      key={currentImportStep}
      ref={currProjectComponentsListRef}
      dialogTitle={componentName}
      formattedProjects={
        projects?.pages.map((project, index) =>
          index === projects?.pages.length - 1 ? { ...project, ref: lastProjectRef } : project,
        ) ?? []
      }
      componentsList={componentsRes?.data}
      numOfTotalSelectedComponents={numOfTotalSelectedComponents}
      isProjectsLoading={isProjectsLoading}
      isProjectsFetchingNextPage={isFetchingNextPage}
      isComponentsListLoading={isComponentsListLoading}
      setIsDialogOpen={setIsDialogOpen}
      importClickHandler={importBtnClickHandler}
      searchInputHandler={searchInputHandler}
      componentsCheckboxChangeHandler={checkboxChangeHandler}
      selectedProject={selectedProject}
      setSelectedProject={setSelectedProject}
      currentComponentsPage={currentProjCompPage}
      setCurrentComponentsPage={setCurrentProjCompPage}
      selectedComponents={selectedComponents}
      setNumberOfPageItems={setNumberOfPageItems}
      numberOfPageItems={numberOfPageItems}
    />,
    <Audit<TVariant>
      key={currentProjectStep}
      componentName={componentName}
      currentProjectStep={currentProjectStep}
      numOfTotalSelectedProjects={numOfTotalSelectedProjects ?? 0}
      numOfCurrentProjComponents={currentEditProjectData?.[1].components.length ?? 0}
      currentEditProjectData={currentEditProjectData}
      componentsDataResults={componentsDataResults}
      removeComponent={removeComponent}
      incrementProjectStep={incrementProjectStep}
      allComponentsDataRef={allComponentsDataRef}
    />,
  ]
  //#endregion

  return (
    <Dialog
      open={isDialogOpen}
      fullScreen={useMediaQuery(MuiTheme.breakpoints.down("md"))}
      fullWidth
      maxWidth="md"
      PaperProps={{ className: styles.dialogContainer }}
    >
      <DialogTitle className={styles.dialogTitle}>
        <Typography variant="h2-bold">Import {t(`copyComponentFlow.componentNames.${componentName}`)}</Typography>
        <IconButton onClick={() => setIsDialogOpen(false)} size="small" className={"close-icon-button"}>
          <CloseIcon style={{ color: theme.palette.gray.text[2] }} />
        </IconButton>
      </DialogTitle>
      <DialogContent className={styles.dialogContent}>{importSteps[currentImportStep - 1]}</DialogContent>
    </Dialog>
  )
}
